import { useAuth, can } from '@/features/auth'
import { useOrgTicketScope } from '@/features/tickets/hooks/useOrgTicketScope'
import { useHasBusinesses } from '@/features/businesses/hooks/useHasBusinesses'
import { SidebarNavGroup } from './SidebarNavGroup'
import { SidebarNavLeaf } from './SidebarNavLeaf'
import type { NavEntry, NavLeaf, NavSectionData } from './navigation'

interface SidebarSectionProps {
  section: NavSectionData
}

function filterEntries(
  items: NavEntry[],
  user: ReturnType<typeof useAuth>['user'],
  hasTicketScope: boolean,
  businessGateOpen: boolean,
): NavEntry[] {
  // Un item se gatea por permiso de rol, por el alcance de tickets (query
  // orgTicketScope) o por tener negocios (requiresBusiness).
  const leafAllowed = (leaf: NavLeaf) => {
    if (leaf.requiresTicketScope && !hasTicketScope) return false
    if (leaf.requiresBusiness && !businessGateOpen) return false
    return !leaf.permission || can(user, leaf.permission)
  }
  const out: NavEntry[] = []
  for (const item of items) {
    if (item.kind === 'leaf') {
      if (leafAllowed(item)) out.push(item)
      continue
    }
    // group: conserva los hijos visibles; si no queda ninguno, oculta el grupo.
    const visibleChildren = item.children.filter(leafAllowed)
    if (visibleChildren.length === 0) continue
    out.push({ ...item, children: visibleChildren })
  }
  return out
}

export function SidebarSection({ section }: SidebarSectionProps) {
  const { user } = useAuth()
  const { hasScope } = useOrgTicketScope()
  const { hasBusinesses, loading: businessesLoading } = useHasBusinesses()
  // Optimista mientras carga: no ocultamos Facturación/Créditos hasta CONFIRMAR
  // que no hay negocios, para que a los clientes con negocios no les parpadee el
  // menú en la primera carga.
  const businessGateOpen = businessesLoading || hasBusinesses
  const items = filterEntries(section.items, user, hasScope, businessGateOpen)
  if (items.length === 0) return null

  return (
    <div>
      <div className="px-3 pb-2 pt-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-subtle">
        {section.title}
      </div>
      {items.map((item) => {
        switch (item.kind) {
          case 'leaf':
            return <SidebarNavLeaf key={item.id} item={item} />
          case 'group':
            return <SidebarNavGroup key={item.id} group={item} />
        }
      })}
    </div>
  )
}
